import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, CardContent, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, TextField,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Alert, Divider,
} from '@mui/material';
import {
  Login as LoginIcon, Logout as LogoutIcon, FilterList,
  Edit as EditIcon, PersonOff, Close, AccessTime, TableChart,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/authService';
import LocationViewer from '../common/LocationViewer';

interface AttendanceRecord {
  id: number;
  technician_id: number;
  date: string;
  clock_in_time: string | null;
  clock_out_time: string | null;
  clock_in_lat: number | null;
  clock_in_lng: number | null;
  clock_out_lat: number | null;
  clock_out_lng: number | null;
  status: 'present' | 'late' | 'absent' | 'half_day' | 'leave';
  working_hours_minutes: number | null;
  notes: string | null;
  admin_notes: string | null;
  technician?: { id: number; user?: { id: number; name: string; name_ar?: string } };
}

interface ReportRow {
  technician_id: number;
  technician_name: string;
  total_days: number;
  present: number;
  late: number;
  absent: number;
  half_day: number;
  leave: number;
  total_hours_minutes: number;
  avg_hours_minutes: number;
}

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  present: 'success', late: 'warning', absent: 'error', half_day: 'info', leave: 'default',
};

const formatMinutes = (mins: number | null) => {
  if (mins == null) return '-';
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const Attendance: React.FC = () => {
  const { can, roles, user } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();

  const canMark = can('attendance.mark');
  const canView = can('attendance.view');
  const canManage = can('attendance.manage');
  // Users created from the Technicians page always get the legacy enum role
  // 'technician' plus the RBAC technician role. Do NOT fall back to having a
  // technician record — the backend auto-created stray records for admins.
  const isTechnician = user?.role === 'technician' || roles.some((r) => r.toLowerCase().includes('techni'));

  // A field technician (can mark attendance but is not a manager) gets a simple
  // clock-in/out screen with their own history — no report, stats or filters.
  // Managers/admins (or read-only viewers) get the full report view instead.
  const isMarker = canMark && !canManage;

  // Today status
  const [todayData, setTodayData] = useState<{ attendance: AttendanceRecord | null; technician_id: number | null; shift_start: string; shift_end: string } | null>(null);
  // A manager/admin who also holds the technician role gets a "My Attendance"
  // tab alongside the report view so they can clock in/out themselves.
  const showMyTab = canMark && (!canManage || isTechnician);

  // Build tabs array based on permissions + technician status
  const tabs: { key: string; label: string; icon: React.ReactElement }[] = [];
  if (showMyTab) tabs.push({ key: 'my', label: t('attendance.tabs.my'), icon: <AccessTime /> });
  if (canView || canManage) tabs.push({ key: 'details', label: t('attendance.details'), icon: <TableChart /> });

  const [activeTab, setActiveTab] = useState(0);
  const currentTabKey = tabs[activeTab]?.key || '';
  const [clockLoading, setClockLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // My history
  const [myRecords, setMyRecords] = useState<AttendanceRecord[]>([]);
  const [myPage, setMyPage] = useState(1);
  const [myTotalPages, setMyTotalPages] = useState(1);
  const [myLoading, setMyLoading] = useState(false);

  // All records
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsTotalPages, setRecordsTotalPages] = useState(1);
  const [filterTechId, setFilterTechId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Technicians list
  const [technicians, setTechnicians] = useState<any[]>([]);

  // Today stats (admin)
  const [todayStats, setTodayStats] = useState<ReportRow[]>([]);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<AttendanceRecord | null>(null);
  const [editForm, setEditForm] = useState({ status: '', admin_notes: '', clock_in_time: '', clock_out_time: '' });

  // Mark absent dialog
  const [absentOpen, setAbsentOpen] = useState(false);
  const [absentForm, setAbsentForm] = useState({ technician_id: '', date: '', admin_notes: '' });

  // Report summary (fetched alongside records)
  const [reportData, setReportData] = useState<ReportRow[]>([]);

  useEffect(() => {
    if (showMyTab) { fetchTodayStatus(); fetchMyRecords(1); }
    if (canView || canManage) { fetchTechnicians(); fetchTodayStats(); }
  }, []);

  const fetchTodayStats = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.get(`/attendance/report?date_from=${today}&date_to=${today}`);
      setTodayStats(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const fetchTodayStatus = async () => {
    try {
      const res = await api.get('/attendance/today');
      setTodayData(res.data);
      startTimer(res.data.attendance);
    } catch (e) { console.error(e); }
  };

  const startTimer = (att: AttendanceRecord | null) => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (att?.clock_in_time && !att?.clock_out_time) {
      const update = () => {
        const diff = Math.floor((Date.now() - new Date(att.clock_in_time!).getTime()) / 1000);
        setElapsedSeconds(diff > 0 ? diff : 0);
      };
      update();
      timerRef.current = setInterval(update, 1000);
    } else { setElapsedSeconds(0); }
  };

  const fetchMyRecords = async (page = 1) => {
    try {
      setMyLoading(true);
      const res = await api.get(`/attendance/my?page=${page}&per_page=10`);
      setMyRecords(res.data.data || []);
      setMyTotalPages(res.data.last_page || 1);
      setMyPage(res.data.current_page || page);
    } catch (e) { console.error(e); }
    finally { setMyLoading(false); }
  };

  const getLocation = (): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error('GPS not supported'));
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  const handleClockIn = async () => {
    try {
      setClockLoading(true);
      const loc = await getLocation();
      await api.post('/attendance/clock-in', { lat: loc.lat, lng: loc.lng });
      notifySuccess(t('attendance.clockedInSuccess'));
      fetchTodayStatus();
      fetchMyRecords(1);
    } catch (e: any) {
      notifyError(e?.response?.data?.error || e?.message || t('attendance.gps.error'));
    } finally { setClockLoading(false); }
  };

  const handleClockOut = async () => {
    try {
      setClockLoading(true);
      const loc = await getLocation();
      await api.post('/attendance/clock-out', { lat: loc.lat, lng: loc.lng });
      notifySuccess(t('attendance.clockedOutSuccess'));
      if (timerRef.current) clearInterval(timerRef.current);
      fetchTodayStatus();
      fetchMyRecords(1);
    } catch (e: any) {
      notifyError(e?.response?.data?.error || e?.message || t('attendance.gps.error'));
    } finally { setClockLoading(false); }
  };

  const fetchTechnicians = async () => {
    try {
      const res = await api.get('/technicians');
      setTechnicians(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (e) { console.error(e); }
  };

  const fetchRecords = async (page = 1) => {
    try {
      setRecordsLoading(true);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('per_page', '15');
      if (filterTechId) params.append('technician_id', filterTechId);
      if (filterStatus) params.append('status', filterStatus);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      // Fetch records + summary in parallel
      const reportParams = new URLSearchParams();
      if (filterDateFrom) reportParams.append('date_from', filterDateFrom);
      if (filterDateTo) reportParams.append('date_to', filterDateTo);
      if (filterTechId) reportParams.append('technician_id', filterTechId);

      const [recordsRes, reportRes] = await Promise.all([
        api.get(`/attendance?${params.toString()}`),
        canView ? api.get(`/attendance/report?${reportParams.toString()}`) : Promise.resolve({ data: [] }),
      ]);

      setRecords(recordsRes.data.data || []);
      setRecordsTotalPages(recordsRes.data.last_page || 1);
      setRecordsPage(recordsRes.data.current_page || page);
      setReportData(Array.isArray(reportRes.data) ? reportRes.data : []);
    } catch (e) { console.error(e); }
    finally { setRecordsLoading(false); }
  };

  const openEdit = (rec: AttendanceRecord) => {
    setEditRecord(rec);
    setEditForm({
      status: rec.status,
      admin_notes: rec.admin_notes || '',
      clock_in_time: rec.clock_in_time ? rec.clock_in_time.slice(0, 16) : '',
      clock_out_time: rec.clock_out_time ? rec.clock_out_time.slice(0, 16) : '',
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editRecord) return;
    try {
      await api.put(`/attendance/${editRecord.id}`, editForm);
      notifySuccess(t('attendance.updated'));
      setEditOpen(false);
      fetchRecords(recordsPage);
    } catch (e: any) {
      notifyError(e?.response?.data?.error || 'Failed to update');
    }
  };

  const submitAbsent = async () => {
    try {
      await api.post('/attendance/mark-absent', absentForm);
      notifySuccess(t('attendance.markedAbsent'));
      setAbsentOpen(false);
      if (currentTabKey === 'details') fetchRecords(recordsPage);
    } catch (e: any) {
      notifyError(e?.response?.data?.error || 'Failed to mark absent');
    }
  };

  const getTechName = (tech: any) => {
    if (!tech) return 'Unknown';
    return language === 'ar' && tech.user?.name_ar ? tech.user.name_ar : (tech.user?.name || tech.name || 'Unknown');
  };

  const formatDT = (dt: string | null) => {
    if (!dt) return '-';
    return new Date(dt).toLocaleString(language === 'ar' ? 'ar' : 'en', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kuwait' });
  };

  const formatDate = (dt: string) => {
    return new Date(dt).toLocaleDateString(language === 'ar' ? 'ar' : 'en', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Asia/Kuwait' });
  };

  const elapsedStr = () => {
    const h = Math.floor(elapsedSeconds / 3600);
    const m = Math.floor((elapsedSeconds % 3600) / 60);
    const s = elapsedSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1.5}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', sm: '2.125rem' }, fontWeight: 700 }}>{t('attendance.title')}</Typography>
        {canManage && (
          <Button variant="outlined" startIcon={<PersonOff />} sx={{ whiteSpace: 'nowrap' }} onClick={() => { setAbsentForm({ technician_id: '', date: new Date().toISOString().slice(0, 10), admin_notes: '' }); setAbsentOpen(true); }}>
            {t('attendance.markAbsent')}
          </Button>
        )}
      </Box>

      {/* Tab bar — shown when the user has both a personal and a report view */}
      {!isMarker && tabs.length > 1 && (
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>
      )}

      {/* ===== Technician: My Attendance (simple clock-in/out, no report) ===== */}
      {showMyTab && (isMarker || currentTabKey === 'my') && (
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
            {/* Gradient header */}
            <Box sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', px: { xs: 2.5, sm: 3 }, py: { xs: 2, sm: 2.5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box display="flex" alignItems="center" gap={1.25}>
                <AccessTime />
                <Typography variant="h6" fontWeight={700}>{t('attendance.todayStatus')}</Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{formatDate(new Date().toISOString().slice(0, 10))}</Typography>
            </Box>

            <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
              {!todayData ? (
                <Box textAlign="center" py={3}><CircularProgress size={30} /></Box>
              ) : !todayData.attendance?.clock_in_time ? (
                <Box textAlign="center">
                  <Alert severity="info" sx={{ mb: 3, justifyContent: 'center' }}>{t('attendance.notClockedIn')}</Alert>
                  <Button fullWidth variant="contained" color="success" size="large" startIcon={clockLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />} onClick={handleClockIn} disabled={clockLoading} sx={{ py: 1.75, fontSize: '1.05rem', fontWeight: 700, borderRadius: 2, maxWidth: 420, mx: 'auto' }}>{t('attendance.clockIn')}</Button>
                </Box>
              ) : !todayData.attendance?.clock_out_time ? (
                <Box textAlign="center">
                  <Box display="flex" justifyContent="center" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                    <Chip label={t(`attendance.status.${todayData.attendance.status}`)} color={statusColors[todayData.attendance.status]} />
                    <Typography variant="body2" color="text.secondary">{t('attendance.clockedIn')} {formatDT(todayData.attendance.clock_in_time)}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'monospace', my: { xs: 2, sm: 3 }, fontWeight: 'bold', fontSize: { xs: '2.75rem', sm: '4rem' }, lineHeight: 1, letterSpacing: 1, color: 'primary.main' }}>{elapsedStr()}</Typography>
                  <Box sx={{ mb: 3, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">{t('attendance.columns.clockInLocation')}:</Typography>
                    <LocationViewer lat={todayData.attendance.clock_in_lat} lng={todayData.attendance.clock_in_lng} title={t('attendance.columns.clockInLocation')} />
                  </Box>
                  <Button fullWidth variant="contained" color="error" size="large" startIcon={clockLoading ? <CircularProgress size={20} color="inherit" /> : <LogoutIcon />} onClick={handleClockOut} disabled={clockLoading} sx={{ py: 1.75, fontSize: '1.05rem', fontWeight: 700, borderRadius: 2, maxWidth: 420, mx: 'auto' }}>{t('attendance.clockOut')}</Button>
                </Box>
              ) : (
                <Box>
                  <Alert severity="success" sx={{ mb: 3, justifyContent: 'center' }}>{t('attendance.clockedOut')}</Alert>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                    {[
                      { label: t('attendance.columns.clockIn'), value: formatDT(todayData.attendance.clock_in_time) },
                      { label: t('attendance.columns.clockOut'), value: formatDT(todayData.attendance.clock_out_time) },
                      { label: t('attendance.columns.hours'), value: formatMinutes(todayData.attendance.working_hours_minutes) },
                    ].map((item, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{item.value}</Typography>
                      </Paper>
                    ))}
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block">{t('attendance.columns.status')}</Typography>
                      <Box mt={0.5}><Chip size="small" label={t(`attendance.status.${todayData.attendance.status}`)} color={statusColors[todayData.attendance.status]} /></Box>
                    </Paper>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, mt: 2 }}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>{t('attendance.columns.clockInLocation')}</Typography>
                      <LocationViewer lat={todayData.attendance.clock_in_lat} lng={todayData.attendance.clock_in_lng} title={t('attendance.columns.clockInLocation')} />
                    </Paper>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>{t('attendance.columns.clockOutLocation')}</Typography>
                      <LocationViewer lat={todayData.attendance.clock_out_lat} lng={todayData.attendance.clock_out_lng} title={t('attendance.columns.clockOutLocation')} />
                    </Paper>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom fontWeight={700}>{t('attendance.myHistory')}</Typography>
              {myLoading ? (
                <Box textAlign="center" py={3}><CircularProgress size={30} /></Box>
              ) : myRecords.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={3}>{t('attendance.noRecords')}</Typography>
              ) : (
                <>
                  {/* Desktop / tablet: table */}
                  <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, maxHeight: 'calc(100vh - 450px)', minHeight: 200, borderRadius: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead><TableRow>
                        <TableCell><strong>{t('attendance.columns.date')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockIn')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockInLocation')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockOut')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockOutLocation')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.hours')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.status')}</strong></TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {myRecords.map((rec) => (
                          <TableRow key={rec.id} hover>
                            <TableCell>{formatDate(rec.date)}</TableCell>
                            <TableCell>{formatDT(rec.clock_in_time)}</TableCell>
                            <TableCell><LocationViewer lat={rec.clock_in_lat} lng={rec.clock_in_lng} title={t('attendance.columns.clockInLocation')} /></TableCell>
                            <TableCell>{formatDT(rec.clock_out_time)}</TableCell>
                            <TableCell><LocationViewer lat={rec.clock_out_lat} lng={rec.clock_out_lng} title={t('attendance.columns.clockOutLocation')} /></TableCell>
                            <TableCell>{formatMinutes(rec.working_hours_minutes)}</TableCell>
                            <TableCell><Chip label={t(`attendance.status.${rec.status}`)} color={statusColors[rec.status]} size="small" /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Mobile: card list */}
                  <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                    {myRecords.map((rec) => (
                      <Paper key={rec.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Typography fontWeight={700}>{formatDate(rec.date)}</Typography>
                          <Chip label={t(`attendance.status.${rec.status}`)} color={statusColors[rec.status]} size="small" />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">{t('attendance.columns.clockIn')}</Typography>
                            <Typography variant="body2">{formatDT(rec.clock_in_time)}</Typography>
                            <Box mt={0.25}><LocationViewer lat={rec.clock_in_lat} lng={rec.clock_in_lng} title={t('attendance.columns.clockInLocation')} /></Box>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">{t('attendance.columns.clockOut')}</Typography>
                            <Typography variant="body2">{formatDT(rec.clock_out_time)}</Typography>
                            <Box mt={0.25}><LocationViewer lat={rec.clock_out_lat} lng={rec.clock_out_lng} title={t('attendance.columns.clockOutLocation')} /></Box>
                          </Box>
                        </Box>
                        <Box mt={1.25} pt={1.25} sx={{ borderTop: '1px dashed', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">{t('attendance.columns.hours')}</Typography>
                          <Typography variant="body2" fontWeight={600}>{formatMinutes(rec.working_hours_minutes)}</Typography>
                        </Box>
                      </Paper>
                    ))}
                  </Box>

                  {myTotalPages > 1 && <Box display="flex" justifyContent="center" mt={2}><Pagination count={myTotalPages} page={myPage} onChange={(_, p) => fetchMyRecords(p)} color="primary" size="small" /></Box>}
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ===== Admin / Report View (managers & read-only viewers, not field technicians) ===== */}
      {!isMarker && (canView || canManage) && currentTabKey === 'details' && (
        <>
          {/* Today's Statistics — top of page */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" fontWeight="bold">{todayStats.reduce((s, r) => s + r.present, 0)}</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>{t('attendance.today.present')}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" fontWeight="bold">{todayStats.reduce((s, r) => s + r.late, 0)}</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>{t('attendance.today.late')}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" fontWeight="bold">{todayStats.reduce((s, r) => s + r.absent, 0)}</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>{t('attendance.today.absent')}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" fontWeight="bold">{todayStats.reduce((s, r) => s + r.total_days, 0)}</Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>{t('attendance.today.total')}</Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Filters */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, alignItems: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.filter.technician')}</InputLabel>
                  <Select value={filterTechId} label={t('attendance.filter.technician')} onChange={(e) => setFilterTechId(e.target.value)}>
                    <MenuItem value="">{t('reports.filters.technician.all')}</MenuItem>
                    {technicians.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id.toString()}>{getTechName(tech)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('attendance.filter.status')}</InputLabel>
                  <Select value={filterStatus} label={t('attendance.filter.status')} onChange={(e) => setFilterStatus(e.target.value)}>
                    <MenuItem value="">{t('reports.filters.status.all')}</MenuItem>
                    {['present', 'late', 'absent', 'half_day', 'leave'].map(s => (
                      <MenuItem key={s} value={s}>{t(`attendance.status.${s}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField size="small" type="date" label={t('attendance.filter.dateFrom')} value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label={t('attendance.filter.dateTo')} value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Button variant="contained" onClick={() => fetchRecords(1)} disabled={recordsLoading} startIcon={recordsLoading ? <CircularProgress size={20} /> : <FilterList />}>
                  {t('reports.filters.generate')}
                </Button>
              </Box>
            </CardContent>
          </Card>

      {/* ===== Attendance Details ===== */}
        <Box>
          {records.length > 0 && (
            <Card>
              <CardContent>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 350px)', minHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('attendance.columns.date')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.technician')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockIn')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockInLocation')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockOut')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.clockOutLocation')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.status')}</strong></TableCell>
                        <TableCell><strong>{t('attendance.columns.hours')}</strong></TableCell>
                        {canManage && <TableCell><strong>{t('attendance.columns.actions')}</strong></TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map((rec) => (
                        <TableRow key={rec.id} hover>
                          <TableCell>{formatDate(rec.date)}</TableCell>
                          <TableCell>{getTechName(rec.technician)}</TableCell>
                          <TableCell>{formatDT(rec.clock_in_time)}</TableCell>
                          <TableCell><LocationViewer lat={rec.clock_in_lat} lng={rec.clock_in_lng} title={t('attendance.columns.clockInLocation')} /></TableCell>
                          <TableCell>{formatDT(rec.clock_out_time)}</TableCell>
                          <TableCell><LocationViewer lat={rec.clock_out_lat} lng={rec.clock_out_lng} title={t('attendance.columns.clockOutLocation')} /></TableCell>
                          <TableCell><Chip label={t(`attendance.status.${rec.status}`)} color={statusColors[rec.status]} size="small" /></TableCell>
                          <TableCell>{formatMinutes(rec.working_hours_minutes)}</TableCell>
                          {canManage && (
                            <TableCell>
                              <IconButton size="small" onClick={() => openEdit(rec)}><EditIcon fontSize="small" /></IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {recordsTotalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination count={recordsTotalPages} page={recordsPage} onChange={(_, p) => fetchRecords(p)} color="primary" />
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Box>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('attendance.editTitle')}
          <IconButton onClick={() => setEditOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('attendance.columns.status')}</InputLabel>
              <Select value={editForm.status} label={t('attendance.columns.status')} onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}>
                {['present', 'late', 'absent', 'half_day', 'leave'].map(s => (
                  <MenuItem key={s} value={s}>{t(`attendance.status.${s}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t('attendance.columns.clockIn')} type="datetime-local" value={editForm.clock_in_time} onChange={(e) => setEditForm(p => ({ ...p, clock_in_time: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label={t('attendance.columns.clockOut')} type="datetime-local" value={editForm.clock_out_time} onChange={(e) => setEditForm(p => ({ ...p, clock_out_time: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label={t('attendance.adminNotes')} value={editForm.admin_notes} onChange={(e) => setEditForm(p => ({ ...p, admin_notes: e.target.value }))} multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('reports.filters.clear')}</Button>
          <Button variant="contained" onClick={submitEdit}>{t('attendance.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Mark Absent Dialog */}
      <Dialog open={absentOpen} onClose={() => setAbsentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('attendance.markAbsent')}
          <IconButton onClick={() => setAbsentOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>{t('attendance.columns.technician')}</InputLabel>
              <Select value={absentForm.technician_id} label={t('attendance.columns.technician')} onChange={(e) => setAbsentForm(p => ({ ...p, technician_id: e.target.value }))}>
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id.toString()}>{getTechName(tech)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label={t('attendance.columns.date')} type="date" value={absentForm.date} onChange={(e) => setAbsentForm(p => ({ ...p, date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label={t('attendance.adminNotes')} value={absentForm.admin_notes} onChange={(e) => setAbsentForm(p => ({ ...p, admin_notes: e.target.value }))} multiline rows={2} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbsentOpen(false)}>{t('reports.filters.clear')}</Button>
          <Button variant="contained" color="error" onClick={submitAbsent}>{t('attendance.markAbsent')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Attendance;
